import React, { useMemo } from 'react';
import { Card, Table, Statistic, Row, Col, Empty, Skeleton, Tag, Button, Dropdown } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useResponsive } from '../../../hooks/useResponsive';
import { useCurrency } from '../../../hooks/useCurrency';
import { useGetWithdrawalBreakdownQuery } from '../../../services/reportsApi';
import { useAppSelector } from '../../../hooks/redux';
import { exportCSV, exportExcel, exportPDF, getPeriodLabel, ExportConfig } from '../../../utils/reportExport';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';

interface WithdrawalBreakdownProps {
  restaurantId: string;
  fromDate: string;
  toDate: string;
  selectedPeriod?: string;
  customDateRange?: [any, any] | null;
}

const TYPE_LABELS: Record<string, string> = {
  DROP: 'Retiro',
  EXPENSE: 'Gasto',
  PAYOUT: 'Pago',
  CORRECTION: 'Corrección',
};

const WithdrawalBreakdown: React.FC<WithdrawalBreakdownProps> = ({
  restaurantId,
  fromDate,
  toDate,
  selectedPeriod,
  customDateRange,
}) => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const { data, isLoading } = useGetWithdrawalBreakdownQuery(
    { restaurantId, fromDate, toDate },
    { skip: !restaurantId || !fromDate || !toDate }
  );

  const reasonFilters = useMemo(() => {
    if (!data?.movements) return [];
    return Array.from(new Set(data.movements.map((m) => m.reason)))
      .map((r) => ({ text: r, value: r }));
  }, [data]);

  const userFilters = useMemo(() => {
    if (!data?.movements) return [];
    return Array.from(new Set(data.movements.map((m) => m.userName)))
      .map((u) => ({ text: u, value: u }));
  }, [data]);

  const typeFilters = useMemo(() => {
    if (!data?.movements) return [];
    return Array.from(new Set(data.movements.map((m) => m.type)))
      .map((t) => ({ text: TYPE_LABELS[t] || t, value: t }));
  }, [data]);

  const handleExportGrid = (format: 'csv' | 'excel' | 'pdf') => {
    if (!data?.movements || data.movements.length === 0) return;

    const periodLabel = getPeriodLabel(selectedPeriod || 'today', customDateRange);
    const config: ExportConfig = {
      reportTitle: 'Retiros e Ingresos de Caja',
      periodLabel,
      restaurantName: currentRestaurant?.name || 'N/A',
      timestamp: formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
      summaryCards: [
        { label: 'Total Retiros', value: `${currencySymbol} ${data.totalWithdrawals.toFixed(2)}` },
        { label: 'Total Correcciones Positivas', value: `${currencySymbol} ${data.totalIncome.toFixed(2)}` },
        { label: 'Movimientos', value: `${data.totalCount}` },
      ],
      sections: [{
        title: 'Detalle de Movimientos',
        titleStyle: 'cash',
        headers: ['Fecha', 'Dirección', 'Tipo', 'Motivo', 'Monto', 'Cajero', 'Referencia'],
        rows: data.movements.map((m) => [
          formatInTimeZone(m.createdAt, 'DD/MM/YYYY HH:mm'),
          m.direction,
          TYPE_LABELS[m.type] || m.type,
          m.reason,
          `${currencySymbol} ${m.amount.toFixed(2)}`,
          m.userName,
          m.reference || '-',
        ]),
      }],
    };

    if (format === 'csv') exportCSV(config, 'retiros-ingresos');
    else if (format === 'excel') exportExcel(config, 'retiros-ingresos');
    else exportPDF(config);
  };

  if (isLoading) {
    return <Card><Skeleton active paragraph={{ rows: 8 }} /></Card>;
  }

  if (!data || data.totalCount === 0) {
    return <Card><Empty description="No hay retiros ni ingresos en este período" /></Card>;
  }

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (value: string) => formatInTimeZone(value, 'DD/MM/YYYY HH:mm'),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Dirección',
      dataIndex: 'direction',
      key: 'direction',
      filters: [
        { text: 'Retiro', value: 'Retiro' },
        { text: 'Ingreso', value: 'Ingreso' },
      ],
      onFilter: (value: any, record: any) => record.direction === value,
      render: (value: string) => (
        <Tag color={value === 'Retiro' ? 'red' : 'green'}>{value}</Tag>
      ),
      width: 110,
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      filters: typeFilters,
      onFilter: (value: any, record: any) => record.type === value,
      render: (value: string) => TYPE_LABELS[value] || value,
      width: 110,
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      key: 'reason',
      filters: reasonFilters,
      onFilter: (value: any, record: any) => record.reason === value,
      ellipsis: true,
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      key: 'amount',
      sorter: (a: any, b: any) => a.amount - b.amount,
      render: (value: number, record: any) => (
        <span style={{ color: record.direction === 'Retiro' ? 'var(--color-error-text)' : 'var(--color-success-text)', fontWeight: 500 }}>
          {record.direction === 'Retiro' ? '-' : '+'} {currencySymbol} {value.toFixed(2)}
        </span>
      ),
      align: 'right' as const,
    },
    {
      title: 'Cajero',
      dataIndex: 'userName',
      key: 'userName',
      filters: userFilters,
      onFilter: (value: any, record: any) => record.userName === value,
    },
    {
      title: 'Referencia',
      dataIndex: 'reference',
      key: 'reference',
      render: (value: string | null) => value || '-',
      responsive: ['md'] as any,
    },
  ];

  const exportMenuItems = [
    { key: 'csv', label: 'CSV', onClick: () => handleExportGrid('csv') },
    { key: 'excel', label: 'Excel', onClick: () => handleExportGrid('excel') },
    { key: 'pdf', label: 'PDF', onClick: () => handleExportGrid('pdf') },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Retiros"
              value={data.totalWithdrawals}
              precision={2}
              prefix={currencySymbol}
              valueStyle={{ color: 'var(--color-error-text)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Correcciones Positivas"
              value={data.totalIncome}
              precision={2}
              prefix={currencySymbol}
              valueStyle={{ color: 'var(--color-success-text)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Movimientos"
              value={data.totalCount}
              suffix={<span style={{ fontSize: 14, color: 'var(--color-neutral-text)' }}>
                ({data.withdrawalCount} retiros, {data.incomeCount} ingresos)
              </span>}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Dropdown menu={{ items: exportMenuItems }}>
            <Button icon={<DownloadOutlined />} size="small">
              Exportar este grid
            </Button>
          </Dropdown>
        </div>
        <Table
          columns={columns}
          dataSource={data.movements}
          rowKey="id"
          pagination={{ defaultPageSize: 15, showSizeChanger: true }}
          scroll={isMobile ? { x: 800 } : undefined}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default WithdrawalBreakdown;
